import { Fragment, type ReactNode } from 'react';

/**
 * Renders the small Markdown subset start-page text is written in, as React elements only: the text is
 * authored in the hosting portal and shown to anonymous visitors, so it never becomes HTML.
 *
 * - a blank line starts a new paragraph, a single line break stays a line break;
 * - `## ` and `### ` start a sub-heading;
 * - lines starting `- ` or `* ` form a bulleted list, lines starting `1. ` a numbered one;
 * - `**bold**` and `[label](https://…)` inline. Links may be http(s), `mailto:` or a `#anchor` on the
 *   page; anything else renders as its label alone.
 */
export function RichText({ text, className }: { text: string; className?: string | undefined }) {
  return <div className={className}>{parseBlocks(text)}</div>;
}

const BULLET = /^[-*]\s+/;
const NUMBERED = /^\d+[.)]\s+/;
const HEADING = /^(#{2,3})\s+/;

function parseBlocks(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const groups = text.replace(/\r\n?/g, '\n').split(/\n\s*\n/);

  groups.forEach((group, groupIndex) => {
    const lines = group.split('\n').filter((line) => line.trim() !== '');
    let paragraph: string[] = [];
    let list: { ordered: boolean; items: string[] } | null = null;

    const flushParagraph = () => {
      if (paragraph.length === 0) return;
      const key = `p-${groupIndex}-${nodes.length}`;
      nodes.push(
        <p key={key}>
          {paragraph.map((line, index) => (
            <Fragment key={index}>
              {index > 0 && <br />}
              {parseInline(line)}
            </Fragment>
          ))}
        </p>,
      );
      paragraph = [];
    };

    const flushList = () => {
      if (!list) return;
      const key = `l-${groupIndex}-${nodes.length}`;
      const items = list.items.map((item, index) => <li key={index}>{parseInline(item)}</li>);
      nodes.push(list.ordered ? <ol key={key}>{items}</ol> : <ul key={key}>{items}</ul>);
      list = null;
    };

    for (const raw of lines) {
      const line = raw.trim();
      const heading = HEADING.exec(line);
      const ordered = NUMBERED.test(line);

      if (heading) {
        flushParagraph();
        flushList();
        const content = parseInline(line.slice(heading[0].length));
        const key = `h-${groupIndex}-${nodes.length}`;
        nodes.push(heading[1] === '##' ? <h3 key={key}>{content}</h3> : <h4 key={key}>{content}</h4>);
      } else if (BULLET.test(line) || ordered) {
        flushParagraph();
        if (list && list.ordered !== ordered) flushList();
        list ??= { ordered, items: [] };
        list.items.push(line.replace(ordered ? NUMBERED : BULLET, ''));
      } else {
        flushList();
        paragraph.push(line);
      }
    }

    flushParagraph();
    flushList();
  });

  return nodes;
}

const INLINE = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/g;

function parseInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let last = 0;

  for (const match of text.matchAll(INLINE)) {
    const index = match.index ?? 0;
    if (index > last) nodes.push(text.slice(last, index));

    const [, bold, label, href] = match;
    if (bold !== undefined) {
      nodes.push(<strong key={index}>{bold}</strong>);
    } else if (label !== undefined) {
      nodes.push(renderLink(label, href ?? '', index));
    }

    last = index + match[0].length;
  }

  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function renderLink(label: string, href: string, key: number): ReactNode {
  if (href.startsWith('#')) {
    return <a key={key} href={href}>{label}</a>;
  }

  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return label;
  }

  if (url.protocol === 'mailto:') {
    return <a key={key} href={url.href}>{label}</a>;
  }

  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    return label;
  }

  return (
    <a key={key} href={url.href} target="_blank" rel="noopener noreferrer">
      {label}
    </a>
  );
}
